import {
  get,
  set,
  push,
  update,
  remove,
  serverTimestamp,
  customersRef,
  customerRef,
  snapshotToArray,
  snapshotToItem,
} from '@/firebase/database';
import type { Customer, CustomerFormData } from '@/types';
import { createNotification } from './notificationService';

export const getCustomers = async (companyId: string): Promise<Customer[]> => {
  const snap = await get(customersRef(companyId));
  const list = snapshotToArray<Customer>(snap);
  return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
};

export const getActiveCustomers = async (companyId: string): Promise<Customer[]> => {
  const all = await getCustomers(companyId);
  return all.filter(c => c.status === 'active');
};

export const getCustomerById = async (companyId: string, id: string): Promise<Customer | null> => {
  const snap = await get(customerRef(companyId, id));
  if (!snap.exists()) return null;
  return snapshotToItem<Customer>(snap);
};

export const createCustomer = async (companyId: string, data: CustomerFormData): Promise<string> => {
  const newRef = push(customersRef(companyId));
  const id = newRef.key as string;
  await set(newRef, {
    ...data,
    id,
    companyId,
    status: 'active',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  createNotification({
    companyId,
    category: 'customer',
    type: 'customer_created',
    title: 'Customer Added',
    message: `${data.name} (${data.customerCode || 'Customer'}) was registered`,
    link: `/customers`,
  }).catch(() => {});

  return id;
};

export const updateCustomer = async (
  companyId: string,
  id: string,
  data: Partial<CustomerFormData>
): Promise<void> => {
  await update(customerRef(companyId, id), {
    ...data,
    updatedAt: serverTimestamp(),
  });

  createNotification({
    companyId,
    category: 'customer',
    type: 'customer_updated',
    title: 'Customer Updated',
    message: `Profile updated for ${data.name || 'Customer'}`,
    link: `/customers`,
  }).catch(() => {});
};

export const deactivateCustomer = async (companyId: string, id: string): Promise<void> => {
  await update(customerRef(companyId, id), {
    status: 'inactive',
    updatedAt: serverTimestamp(),
  });
};

export const activateCustomer = async (companyId: string, id: string): Promise<void> => {
  await update(customerRef(companyId, id), {
    status: 'active',
    updatedAt: serverTimestamp(),
  });
};

export const deleteCustomer = async (companyId: string, id: string): Promise<void> => {
  await remove(customerRef(companyId, id));
  createNotification({
    companyId,
    category: 'customer',
    type: 'customer_deleted',
    title: 'Customer Removed',
    message: `A customer was removed from the client directory`,
    link: `/customers`,
  }).catch(() => {});
};

export const generateCustomerCode = (count: number): string => {
  return `CUST${String(count + 1).padStart(4, '0')}`;
};
